import { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  IndianRupee,
  Loader2,
  PlayCircle,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import PricingSimulator from "./PricingSimulator";

/**
 * Pricing Health Checker (read-only).
 *
 * Verifies whether the current slab × zone-multiplier configuration is
 * profitable for any (weight, distance) lane.
 * Reads /api/admin/pricing-health/{check,lanes}.
 */

const STATUS_PILL = {
  ok:   { bg: "bg-emerald-50", fg: "text-emerald-700", border: "border-emerald-200", label: "Healthy", icon: CheckCircle2 },
  thin: { bg: "bg-amber-50",   fg: "text-amber-700",   border: "border-amber-200",   label: "Thin",    icon: AlertTriangle },
  loss: { bg: "bg-rose-50",    fg: "text-rose-700",    border: "border-rose-200",    label: "Loss",    icon: TrendingDown },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.ok;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cfg.bg} ${cfg.fg} ${cfg.border}`}
      data-testid={`status-pill-${status}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

/**
 * Compact widget — admin can drop this anywhere (used inside AdminPricing too).
 * Shows a quick check + the standard-lanes table summary.
 */
export function PricingHealthWidget({ refreshKey = 0, compact = false }) {
  const [weight, setWeight] = useState(5);
  const [distance, setDistance] = useState(500);
  const [prepaid, setPrepaid] = useState(true);
  const [check, setCheck] = useState(null);
  const [lanes, setLanes] = useState(null);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingLanes, setLoadingLanes] = useState(false);

  const runCheck = useCallback(async () => {
    setLoadingCheck(true);
    try {
      const { data } = await api.get("/admin/pricing-health/check", {
        params: { weight_kg: weight, distance_km: distance, prepaid },
      });
      setCheck(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to check lane");
    } finally {
      setLoadingCheck(false);
    }
  }, [weight, distance, prepaid]);

  const runLanes = useCallback(async () => {
    setLoadingLanes(true);
    try {
      const { data } = await api.get("/admin/pricing-health/lanes", {
        params: { prepaid },
      });
      setLanes(data);
    } catch (err) {
      // Surface the real reason so prod incidents are diagnosable from the
      // toast itself instead of needing DevTools. Falls back to a generic
      // message when no response body is available.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      const msg = status
        ? `Failed to load lane summary (HTTP ${status}${detail ? ": " + detail : ""})`
        : `Failed to load lane summary${detail ? ": " + detail : ""}`;
      toast.error(msg, { duration: 8000 });
      // eslint-disable-next-line no-console
      console.error("[pricing-health/lanes]", err);
    } finally {
      setLoadingLanes(false);
    }
  }, [prepaid]);

  useEffect(() => {
    runLanes();
    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return (
    <div className="space-y-4" data-testid="pricing-health-widget">
      {/* Banner — visible only when something is bleeding */}
      {lanes && (lanes.any_loss || lanes.any_thin) && (
        <div
          className={`rounded-2xl border p-4 ${
            lanes.any_loss
              ? "border-rose-200 bg-rose-50"
              : "border-amber-200 bg-amber-50"
          }`}
          data-testid="health-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={`h-5 w-5 mt-0.5 ${lanes.any_loss ? "text-rose-600" : "text-amber-600"}`}
            />
            <div>
              <p className={`font-semibold text-sm ${lanes.any_loss ? "text-rose-800" : "text-amber-800"}`}>
                {lanes.any_loss
                  ? `${lanes.summary.loss} standard lane(s) are LOSS-MAKING in worst-case`
                  : `${lanes.summary.thin} standard lane(s) have thin margins`}
              </p>
              <p className={`text-xs mt-1 ${lanes.any_loss ? "text-rose-700" : "text-amber-700"}`}>
                Review the lanes table below. Heavy long-haul typically needs a per-kg-per-km surcharge in addition to slabs.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick check */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="quick-check-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Activity className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-['Outfit'] font-bold text-slate-900">Quick lane check</h3>
            <p className="text-xs text-slate-500">Type a weight + distance, see if you'd make money on it.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Weight (kg)</Label>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              className="mt-1 rounded-xl h-10"
              data-testid="health-weight-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Distance (km)</Label>
            <Input
              type="number"
              min="1"
              value={distance}
              onChange={(e) => setDistance(Number(e.target.value))}
              className="mt-1 rounded-xl h-10"
              data-testid="health-distance-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Payment</Label>
            <select
              className="mt-1 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white"
              value={prepaid ? "prepaid" : "cod"}
              onChange={(e) => setPrepaid(e.target.value === "prepaid")}
              data-testid="health-payment-select"
            >
              <option value="prepaid">Prepaid (Razorpay 2%)</option>
              <option value="cod">COD (no Razorpay)</option>
            </select>
          </div>
          <Button
            onClick={runCheck}
            disabled={loadingCheck}
            className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-10 gap-2"
            data-testid="run-health-check-btn"
          >
            {loadingCheck ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Run check
          </Button>
        </div>

        {check && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="health-result">
            <div className="rounded-xl border border-slate-100 p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                Customer pays · Zone {check.zone} ({check.zone_label})
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Service charges</span>
                  <span><IndianRupee className="inline h-3 w-3" />{check.customer_service_charges}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>GST</span>
                  <span><IndianRupee className="inline h-3 w-3" />{check.customer_gst}</span>
                </div>
                <div className="flex justify-between font-bold text-slate-900 border-t border-slate-100 pt-1 mt-1">
                  <span>Total</span>
                  <span><IndianRupee className="inline h-3 w-3" />{check.customer_total}</span>
                </div>
                {prepaid && (
                  <div className="flex justify-between text-slate-400 text-xs">
                    <span>Razorpay fee (2%)</span>
                    <span>−<IndianRupee className="inline h-3 w-3" />{check.razorpay_fee_estimate}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Op cost (median lane)</p>
              <div className="space-y-1 text-sm font-mono">
                {Object.entries(check.op_cost_breakdown)
                  .filter(([k]) => !k.startsWith("linehaul_rate") && k !== "total")
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between text-slate-600">
                      <span className="capitalize">{k.replace(/_/g, " ")}</span>
                      <span>₹{v}</span>
                    </div>
                  ))}
                <div className="flex justify-between font-bold text-slate-900 border-t border-slate-100 pt-1 mt-1">
                  <span>Op total</span>
                  <span>₹{check.op_cost_median}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 rounded-xl border border-slate-100 p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Margin · per parcel</p>
                <StatusPill status={check.status} />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Best lane</p>
                  <p className={`text-lg font-bold ${check.margin_best >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="margin-best">
                    ₹{check.margin_best}
                  </p>
                </div>
                <div className="border-x border-slate-200">
                  <p className="text-[10px] uppercase text-slate-400">Median (likely)</p>
                  <p className={`text-lg font-bold ${check.margin_median >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="margin-median">
                    ₹{check.margin_median}
                  </p>
                  <p className="text-[10px] text-slate-400">{check.margin_pct_median}% GM</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Worst lane</p>
                  <p className={`text-lg font-bold ${check.margin_worst >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="margin-worst">
                    ₹{check.margin_worst}
                  </p>
                </div>
              </div>
              {check.notes && check.notes.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600 list-disc list-inside">
                  {check.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lanes table */}
      {!compact && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="standard-lanes-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-['Outfit'] font-bold text-slate-900">Standard lanes</h3>
              <p className="text-xs text-slate-500">Auto-flagged status across the lanes the business cares about.</p>
            </div>
            <Button
              onClick={runLanes}
              variant="outline"
              size="sm"
              disabled={loadingLanes}
              className="rounded-xl h-9"
              data-testid="refresh-lanes-btn"
            >
              {loadingLanes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-100">
                  <th className="pb-2 font-semibold">Lane</th>
                  <th className="pb-2 font-semibold text-right">Cust svc</th>
                  <th className="pb-2 font-semibold text-right">Op (med)</th>
                  <th className="pb-2 font-semibold text-right">Margin</th>
                  <th className="pb-2 font-semibold text-right">GM%</th>
                  <th className="pb-2 font-semibold text-right">Worst</th>
                  <th className="pb-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {(lanes?.lanes || []).map((l, idx) => {
                  // Error lanes surface a diagnostic row — skip numeric formatting
                  // so the whole table doesn't blank out when one lane blows up.
                  if (l.status === "error") {
                    return (
                      <tr key={l.label} className="border-b border-slate-50 bg-rose-50/40" data-testid={`lane-row-${idx}`}>
                        <td className="py-2 pr-2 text-slate-800">{l.label}</td>
                        <td colSpan={5} className="py-2 text-xs text-rose-600 font-mono truncate" title={l.error}>
                          {l.error || "Calculation error"}
                        </td>
                        <td className="py-2"><StatusPill status="loss" /></td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={l.label} className="border-b border-slate-50" data-testid={`lane-row-${idx}`}>
                      <td className="py-2 pr-2 text-slate-800">{l.label}</td>
                      <td className="py-2 text-right font-mono">₹{l.customer_service_charges}</td>
                      <td className="py-2 text-right font-mono text-slate-500">₹{l.op_cost_median}</td>
                      <td className={`py-2 text-right font-mono font-bold ${l.margin_median >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        ₹{l.margin_median}
                      </td>
                      <td className="py-2 text-right font-mono text-slate-500">{l.margin_pct_median}%</td>
                      <td className={`py-2 text-right font-mono ${l.margin_worst >= 0 ? "text-slate-600" : "text-rose-500"}`}>
                        ₹{l.margin_worst}
                      </td>
                      <td className="py-2"><StatusPill status={l.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPricingHealth() {
  return (
    <div className="space-y-6" data-testid="admin-pricing-health-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Pricing Health</h1>
        <p className="text-sm text-slate-500 mt-1">
          Read-only sanity check. Verify whether your current slabs are profitable per parcel before changing prices.
        </p>
      </div>

      <PricingHealthWidget />

      <PricingSimulator />
    </div>
  );
}
